export type StartupFailure = {
  stage: string;
  message: string;
  stack?: string;
  at: string;
};

type StartupLogEntry = {
  stage: string;
  at: string;
  details?: Record<string, unknown>;
};

type StartupDiagnostics = {
  logs: StartupLogEntry[];
  currentStage: string;
  failure: StartupFailure | null;
};

declare global {
  interface Window {
    __flextechStartupDiagnostics?: StartupDiagnostics;
    __flextechStartupLog?: typeof startupLog;
  }
}

const MAX_LOG_ENTRIES = 200;

function diagnostics(): StartupDiagnostics {
  window.__flextechStartupDiagnostics ||= {
    logs: [],
    currentStage: "APP_START",
    failure: null
  };

  return window.__flextechStartupDiagnostics;
}

function normalizeError(error: unknown) {
  if (error instanceof Error) return { message: error.message, stack: error.stack };
  if (typeof error === "string") return { message: error };

  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: "Unknown error" };
  }
}

export function startupLog(stage: string, details?: Record<string, unknown>) {
  const entry = { stage, at: new Date().toISOString(), details };
  const state = diagnostics();
  state.currentStage = stage;
  state.logs = [...state.logs, entry].slice(-MAX_LOG_ENTRIES);
  window.__flextechStartupLog = startupLog;
  console.info("STARTUP_STAGE", entry);
}

export function startupError(stage: string, error: unknown, details?: Record<string, unknown>) {
  const normalized = normalizeError(error);
  const failure = {
    stage,
    message: normalized.message || "Unknown startup error",
    stack: normalized.stack,
    at: new Date().toISOString()
  };
  const state = diagnostics();
  state.currentStage = stage;
  state.failure = failure;
  state.logs = [...state.logs, { stage: `${stage}_FAILED`, at: failure.at, details: { ...details, message: failure.message } }].slice(-MAX_LOG_ENTRIES);
  console.error("STARTUP_ERROR", { ...failure, details });
  return failure;
}

export function getStartupDiagnostics() {
  return diagnostics();
}

export function initializeGlobalErrorCapture() {
  const previousOnError = window.onerror;
  const previousUnhandledRejection = window.onunhandledrejection;

  window.onerror = function(message, source, lineno, colno, error) {
    console.error("GLOBAL_ERROR", {
      message,
      source,
      lineno,
      colno,
      stack: error?.stack
    });
    startupError("GLOBAL_ERROR", error || String(message), { source, lineno, colno });

    if (typeof previousOnError === "function") {
      return previousOnError.call(window, message, source, lineno, colno, error);
    }

    return false;
  };

  window.onunhandledrejection = function(event) {
    console.error("UNHANDLED_PROMISE_REJECTION", event.reason);
    startupError("UNHANDLED_PROMISE_REJECTION", event.reason);

    if (typeof previousUnhandledRejection === "function") {
      return previousUnhandledRejection.call(window, event);
    }

    return undefined;
  };
}

export function describeError(error: unknown) {
  return normalizeError(error).message || "Unknown error";
}
